import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../styles/richTextEditor.css";
import LinkModal from "./LinkModal";
import LinkActionPopup from "./LinkActionPopup";
import {
  uploadImage,
  uploadFile,
  IMAGES_BUCKET,
} from "../../api/supabaseStorage";

// ─── Tab-Mention Blot ────────────────────────────────────────────────────────
const EmbedBlot = Quill.import("blots/embed");
class TabMentionBlot extends EmbedBlot {
  static blotName = "tab-mention";
  static tagName = "span";
  static className = "tab-mention";

  static create(data) {
    const node = super.create(data);
    node.setAttribute("data-tab-index", data.tabIndex);
    node.setAttribute("data-tab-name", data.tabName);
    node.setAttribute("contenteditable", "false");
    node.textContent = `@${data.tabName}`;
    return node;
  }

  static value(node) {
    return {
      tabIndex: parseInt(node.getAttribute("data-tab-index"), 10),
      tabName: node.getAttribute("data-tab-name"),
    };
  }
}
if (!Quill.find("tab-mention")) {
  Quill.register(TabMentionBlot);
}
// ─────────────────────────────────────────────────────────────────────────────

const BULLET_LINE_REGEX = /^([\t ]*)([-*•◦○▪▫■‣∙·])\s+(.*)$/;

const escapeHtml = (text) =>
  text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const buildNestedBulletHtmlFromPlainText = (plainText) => {
  const lines = plainText.split(/\r?\n/);
  const parsedItems = [];
  const nonEmptyLines = lines.filter((line) => line.trim().length > 0).length;

  lines.forEach((line) => {
    const match = line.match(BULLET_LINE_REGEX);
    if (!match) {
      if (parsedItems.length > 0 && line.trim().length > 0) {
        parsedItems[parsedItems.length - 1].text += ` ${line.trim()}`;
      }
      return;
    }

    const indentation = match[1].replaceAll("\t", "  ").length;
    const text = match[3].trim();
    parsedItems.push({ indentation, text });
  });

  if (parsedItems.length === 0) return null;

  // Avoid transforming regular text that only contains an occasional bullet-like line.
  if (nonEmptyLines > 0 && parsedItems.length / nonEmptyLines < 0.5)
    return null;

  const indentations = [
    ...new Set(parsedItems.map((item) => item.indentation)),
  ].sort((a, b) => a - b);
  const indentationSteps = indentations
    .map((value, idx) => (idx === 0 ? null : value - indentations[idx - 1]))
    .filter((step) => step && step > 0);
  const indentUnit =
    indentationSteps.length > 0 ? Math.min(...indentationSteps) : 2;

  const nodes = [];
  const stack = [{ level: -1, children: nodes }];

  parsedItems.forEach((item) => {
    const level = Math.max(0, Math.round(item.indentation / indentUnit));
    while (stack.length > 1 && level <= stack[stack.length - 1].level) {
      stack.pop();
    }

    const node = { text: item.text, children: [] };
    stack[stack.length - 1].children.push(node);
    stack.push({ level, children: node.children });
  });

  const renderList = (listNodes) => {
    const itemsHtml = listNodes
      .map((node) => {
        const childrenHtml =
          node.children.length > 0 ? renderList(node.children) : "";
        return `<li>${escapeHtml(node.text)}${childrenHtml}</li>`;
      })
      .join("");
    return `<ul>${itemsHtml}</ul>`;
  };

  return renderList(nodes);
};

const RichTextEditor = React.forwardRef(
  (
    {
      value,
      onChange,
      placeholder = "Start writing...",
      readOnly = false,
      hideToolbar = false,
      onFocus,
      taskId,
      tabs = [],
      activeTabIndex = -1,
      onTabMentionClick,
    },
    ref,
  ) => {
    const quillRef = useRef(null);
    const wrapperRef = useRef(null);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupPosition, setPopupPosition] = useState(null);
    const [linkContext, setLinkContext] = useState({
      selectedText: "",
      existingLink: null,
      range: null,
    });

    // ── Tab mention autocomplete ──────────────────────────────────────────────
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 });
    const [mentionStartIndex, setMentionStartIndex] = useState(null);
    const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);
    const mentionMenuRef = useRef(null);
    // Stable refs to avoid stale-closure issues inside event listeners
    const mentionOpenRef = useRef(false);
    const mentionQueryRef = useRef("");
    const mentionStartIndexRef = useRef(null);

    // Keep refs in sync with state
    useEffect(() => {
      mentionOpenRef.current = mentionOpen;
    }, [mentionOpen]);
    useEffect(() => {
      mentionQueryRef.current = mentionQuery;
    }, [mentionQuery]);
    useEffect(() => {
      mentionStartIndexRef.current = mentionStartIndex;
    }, [mentionStartIndex]);
    // ─────────────────────────────────────────────────────────────────────────

    // Enhanced link handler that opens modal
    const handleLink = useCallback(() => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const range = quill.getSelection(true);

      if (!range) {
        // If no selection, get current cursor position
        const length = quill.getLength();
        setLinkContext({
          selectedText: "",
          existingLink: null,
          range: { index: length - 1, length: 0 },
        });
        setIsLinkModalOpen(true);
        return;
      }

      const selectedText = quill.getText(range.index, range.length);
      const existingLink = quill.getFormat(range)?.link;

      setLinkContext({
        selectedText,
        existingLink,
        range,
      });
      setIsLinkModalOpen(true);
    }, []);

    // Handle link submission from modal
    const handleLinkSubmit = useCallback(
      (url, linkText) => {
        const quill = quillRef.current?.getEditor();
        if (!quill || !linkContext.range) return;

        const { range } = linkContext;

        if (range.length > 0) {
          // If text is selected, apply link to selection
          quill.formatText(range.index, range.length, "link", url);
          // If linkText is provided and different from selected text, replace it
          if (
            linkText &&
            linkText !== quill.getText(range.index, range.length)
          ) {
            quill.deleteText(range.index, range.length);
            quill.insertText(range.index, linkText, "link", url);
            quill.setSelection(range.index + linkText.length);
          }
        } else {
          // If no text selected, insert link with provided text or URL
          const textToInsert = linkText || url;
          quill.insertText(range.index, textToInsert, "link", url);
          quill.setSelection(range.index + textToInsert.length);
        }
      },
      [linkContext],
    );

    // Handle link removal
    const handleRemoveLink = useCallback(() => {
      const quill = quillRef.current?.getEditor();
      if (!quill || !linkContext.range) return;

      const { range } = linkContext;

      // Remove link formatting while keeping the text
      quill.formatText(range.index, range.length, "link", false);

      // Clear selection
      quill.setSelection(range.index + range.length);
    }, [linkContext]);

    // Prevent Quill's default link tooltip from showing
    useEffect(() => {
      if (!wrapperRef.current) return;

      const removeTooltips = () => {
        // Remove any Quill tooltips/bubbles
        const tooltips = document.querySelectorAll(".ql-tooltip, .ql-bubble");
        tooltips.forEach((tooltip) => {
          if (
            tooltip.closest(".rich-text-editor-wrapper") ||
            tooltip.closest(".ql-container")
          ) {
            tooltip.remove();
          }
        });
      };

      // Use MutationObserver to watch for tooltip creation
      const observer = new MutationObserver(() => {
        removeTooltips();
      });

      const editorWrapper = wrapperRef.current || document.body;
      observer.observe(editorWrapper, {
        childList: true,
        subtree: true,
      });

      // Also remove immediately and periodically as backup
      removeTooltips();
      const interval = setInterval(removeTooltips, 200);

      return () => {
        observer.disconnect();
        clearInterval(interval);
      };
    }, []);

    // Handle link clicks in editor - show popup with Open/Edit options
    useEffect(() => {
      if (readOnly || !quillRef.current) return;

      const quill = quillRef.current.getEditor();
      if (!quill || !quill.root) return;

      const editorElement = quill.root; // Quill's root is the .ql-editor DOM element

      // Handle clicks on links in the editor
      const handleLinkClick = (e) => {
        const linkElement = e.target.closest("a");
        if (linkElement && editorElement.contains(linkElement)) {
          e.preventDefault();
          e.stopPropagation();

          // Remove any Quill tooltips that might appear
          const tooltips = document.querySelectorAll(".ql-tooltip, .ql-bubble");
          tooltips.forEach((tooltip) => tooltip.remove());

          // Get link position for popup
          const rect = linkElement.getBoundingClientRect();

          // Position popup centered above the link
          // Account for scroll position
          const popupX = rect.left + rect.width / 2;
          const popupY = rect.top - 5; // Position slightly above the link (relative to viewport)

          setPopupPosition({ x: popupX, y: popupY });

          // Get link information
          const range = quill.getSelection(true);
          if (range) {
            const format = quill.getFormat(range);
            if (format.link) {
              const selectedText =
                quill.getText(range.index, range.length) ||
                linkElement.textContent;
              const linkUrl = format.link || linkElement.getAttribute("href");

              setLinkContext({
                selectedText,
                existingLink: linkUrl,
                range,
              });
              setIsPopupOpen(true);
            }
          } else {
            // Fallback: get link info from DOM
            const linkUrl = linkElement.getAttribute("href");
            const selectedText = linkElement.textContent;

            // Try to find the range
            const text = editorElement.textContent;
            const linkIndex = text.indexOf(selectedText);

            if (linkIndex !== -1) {
              setLinkContext({
                selectedText,
                existingLink: linkUrl,
                range: { index: linkIndex, length: selectedText.length },
              });
              setIsPopupOpen(true);
            }
          }
        }
      };

      editorElement.addEventListener("click", handleLinkClick, true);
      return () => {
        editorElement.removeEventListener("click", handleLinkClick, true);
      };
    }, [readOnly]);

    // Handle opening the link
    const handleOpenLink = useCallback(() => {
      if (linkContext.existingLink) {
        window.open(linkContext.existingLink, "_blank", "noopener,noreferrer");
      }
    }, [linkContext]);

    // Handle editing the link
    const handleEditLink = useCallback(() => {
      setIsLinkModalOpen(true);
    }, []);

    // Handle image upload to Supabase
    const handleImageUpload = useCallback(() => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");

      input.onchange = async () => {
        const file = input.files[0];
        if (!file) return;

        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        // Show loading cursor
        const range = quill.getSelection(true);
        quill.insertText(range.index, "Uploading image...", {
          color: "var(--faint)",
        });
        quill.setSelection(range.index + 18);

        try {
          // Generate unique path for the image
          const timestamp = Date.now();
          const fileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
          const path = taskId
            ? `tasks/${taskId}/images/${timestamp}-${fileName}`
            : `images/${timestamp}-${fileName}`;

          const { publicUrl, error } = await uploadImage(
            IMAGES_BUCKET,
            path,
            file,
          );

          // Remove loading text
          quill.deleteText(range.index, 18);

          if (error) {
            console.error("Image upload failed:", error);
            quill.insertText(range.index, "❌ Image upload failed", {
              color: "var(--red)",
            });
            return;
          }

          // Insert the image
          quill.insertEmbed(range.index, "image", publicUrl);
          quill.setSelection(range.index + 1);
        } catch (error) {
          console.error("Image upload error:", error);
          quill.deleteText(range.index, 18);
          quill.insertText(range.index, "❌ Upload error", {
            color: "var(--red)",
          });
        }
      };

      input.click();
    }, [taskId]);

    // Handle file/document upload to Supabase
    const handleFileUpload = useCallback(() => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute(
        "accept",
        ".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.csv",
      );

      input.onchange = async () => {
        const file = input.files[0];
        if (!file) return;

        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        // Show loading cursor
        const range = quill.getSelection(true);
        quill.insertText(range.index, "Uploading file...", {
          color: "var(--faint)",
        });
        quill.setSelection(range.index + 16);

        try {
          // Generate unique path for the file
          const timestamp = Date.now();
          const fileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
          const path = taskId
            ? `tasks/${taskId}/files/${timestamp}-${fileName}`
            : `files/${timestamp}-${fileName}`;

          const {
            publicUrl,
            fileName: originalFileName,
            error,
          } = await uploadFile(IMAGES_BUCKET, path, file);

          // Remove loading text
          quill.deleteText(range.index, 16);

          if (error) {
            console.error("File upload failed:", error);
            quill.insertText(range.index, "❌ File upload failed", {
              color: "var(--red)",
            });
            return;
          }

          // Insert the file as a link with file emoji
          const fileText = `📎 ${originalFileName}`;
          quill.insertText(range.index, fileText, "link", publicUrl);
          quill.setSelection(range.index + fileText.length);
        } catch (error) {
          console.error("File upload error:", error);
          quill.deleteText(range.index, 16);
          quill.insertText(range.index, "❌ Upload error", {
            color: "var(--red)",
          });
        }
      };

      input.click();
    }, [taskId]);

    useEffect(() => {
      if (readOnly || !quillRef.current) return;

      const quill = quillRef.current.getEditor();
      const toolbar = quill.getModule("toolbar");

      // Replace default link handler with enhanced one
      if (toolbar) {
        toolbar.addHandler("link", handleLink);
        toolbar.addHandler("image", handleImageUpload);

        // Add custom file upload button handler
        const fileButton = document.querySelector(".ql-file");
        if (fileButton) {
          fileButton.addEventListener("click", handleFileUpload);
        }
      }

      return () => {
        const fileButton = document.querySelector(".ql-file");
        if (fileButton) {
          fileButton.removeEventListener("click", handleFileUpload);
        }
      };
    }, [readOnly, handleLink, handleImageUpload, handleFileUpload]);

    // ── Tab mention: insert embed ─────────────────────────────────────────────
    const insertTabMention = useCallback((tab, tabIdx) => {
      const quill = quillRef.current?.getEditor();
      const startIdx = mentionStartIndexRef.current;
      const query = mentionQueryRef.current;
      if (!quill || startIdx === null) return;

      // Delete the "@query" text the user typed so far, then insert blot
      const deleteLen = 1 + query.length; // "@" + typed chars
      quill.deleteText(startIdx, deleteLen, "user");
      quill.insertEmbed(startIdx, "tab-mention", {
        tabIndex: tabIdx,
        tabName: tab.name,
      });
      // Insert a space after the chip so the cursor lands outside it
      quill.insertText(startIdx + 1, " ", "user");
      quill.setSelection(startIdx + 2);
      setMentionOpen(false);
    }, []);

    // ── Tab mention: detect "@" trigger via text-change ──────────────────────
    useEffect(() => {
      if (readOnly || !quillRef.current) return;
      const quill = quillRef.current.getEditor();
      if (!quill) return;

      const handleTextChange = (_delta, _old, source) => {
        if (source !== "user") return;
        const range = quill.getSelection();
        if (!range) return;

        const textBeforeCursor = quill.getText(0, range.index);
        const atPos = textBeforeCursor.lastIndexOf("@");

        if (atPos === -1) {
          setMentionOpen(false);
          return;
        }

        const textSinceAt = textBeforeCursor.slice(atPos + 1);
        // Dismiss if whitespace appears between @ and cursor
        if (/[\s\n]/.test(textSinceAt)) {
          setMentionOpen(false);
          return;
        }

        setMentionQuery(textSinceAt);
        setMentionStartIndex(atPos);
        setMentionSelectedIdx(0);

        // Position the dropdown near the cursor
        try {
          const bounds = quill.getBounds(range.index);
          const wrapperRect = quill.root
            .closest(".rich-text-editor-wrapper")
            .getBoundingClientRect();
          setMentionPos({
            top: wrapperRect.top + bounds.bottom + window.scrollY + 6,
            left: Math.min(
              wrapperRect.left + bounds.left + window.scrollX,
              window.innerWidth - 240,
            ),
          });
          setMentionOpen(true);
        } catch {
          setMentionOpen(false);
        }
      };

      quill.on("text-change", handleTextChange);
      return () => quill.off("text-change", handleTextChange);
    }, [readOnly]);

    // ── Tab mention: click chips to switch tab ───────────────────────────────
    useEffect(() => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      if (!quill || !quill.root) return;

      const handleMentionClick = (e) => {
        const el = e.target.closest(".tab-mention");
        if (el && quill.root.contains(el)) {
          e.preventDefault();
          e.stopPropagation();
          const idx = parseInt(el.getAttribute("data-tab-index"), 10);
          if (!isNaN(idx) && onTabMentionClick) onTabMentionClick(idx);
        }
      };

      quill.root.addEventListener("click", handleMentionClick, true);
      return () =>
        quill.root.removeEventListener("click", handleMentionClick, true);
    }, [onTabMentionClick]);

    // ── Tab mention: keyboard navigation (arrows / enter / escape) ───────────
    useEffect(() => {
      if (!mentionOpen) return;

      const filteredLen = tabs.filter(
        (t, i) =>
          i !== activeTabIndex &&
          t.name.toLowerCase().includes(mentionQueryRef.current.toLowerCase()),
      ).length;

      const handleKeyDown = (e) => {
        if (!mentionOpenRef.current) return;
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionSelectedIdx((p) => (p + 1) % (filteredLen || 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionSelectedIdx((p) =>
            p === 0 ? (filteredLen || 1) - 1 : p - 1,
          );
        } else if (e.key === "Escape") {
          e.preventDefault();
          setMentionOpen(false);
        }
        // Enter is handled via onMouseDown on the option button to avoid conflicts
      };

      document.addEventListener("keydown", handleKeyDown, true);
      return () => document.removeEventListener("keydown", handleKeyDown, true);
    }, [mentionOpen, tabs, activeTabIndex]);
    // ─────────────────────────────────────────────────────────────────────────

    // Convert pasted plain-text bullets into semantic nested lists.
    useEffect(() => {
      if (readOnly || !quillRef.current) return;

      const quill = quillRef.current.getEditor();
      if (!quill || !quill.root) return;

      const handlePaste = (event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return;

        const html = clipboardData.getData("text/html") || "";
        const plainText = clipboardData.getData("text/plain") || "";
        if (!plainText) return;

        // Let Quill handle rich HTML pastes that already include list semantics.
        if (html && /<li[\s>]/i.test(html)) return;

        const nestedListHtml = buildNestedBulletHtmlFromPlainText(plainText);
        if (!nestedListHtml) return;

        event.preventDefault();

        const currentRange = quill.getSelection(true);
        if (currentRange?.length) {
          quill.deleteText(currentRange.index, currentRange.length, "user");
        }

        const insertIndex = currentRange?.index ?? quill.getLength() - 1;
        quill.clipboard.dangerouslyPasteHTML(
          insertIndex,
          nestedListHtml,
          "user",
        );
      };

      quill.root.addEventListener("paste", handlePaste, true);
      return () => quill.root.removeEventListener("paste", handlePaste, true);
    }, [readOnly]);

    const modules = useMemo(
      () => ({
        toolbar:
          readOnly || hideToolbar
            ? false
            : {
                container: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ indent: "-1" }, { indent: "+1" }],
                  ["blockquote", "code-block"],
                  [{ align: [] }],
                  ["link", "image", "file"],
                ],
                handlers: {
                  link: handleLink,
                  image: handleImageUpload,
                  file: handleFileUpload,
                },
              },
        clipboard: {
          matchVisual: false,
        },
      }),
      [readOnly, hideToolbar, handleLink, handleImageUpload, handleFileUpload],
    );

    const formats = [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "indent",
      "blockquote",
      "code-block",
      "align",
      "link",
      "image",
      "tab-mention",
    ];

    // Add custom icon for file button
    useEffect(() => {
      const icons = Quill.import("ui/icons");
      icons["file"] =
        '<svg viewBox="0 0 18 18"><path d="M11 2H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V6l-4-4zM5 16V4h5v3h3v9H5z" fill="currentColor"/></svg>';
    }, []);

    // Handle focus events
    useEffect(() => {
      if (!quillRef.current || !onFocus) return;

      const quill = quillRef.current.getEditor();
      if (!quill || !quill.root) return;

      const editorElement = quill.root;

      const handleFocus = () => {
        onFocus();
      };

      editorElement.addEventListener("focus", handleFocus, true);
      editorElement.addEventListener("click", handleFocus, true);

      return () => {
        editorElement.removeEventListener("focus", handleFocus, true);
        editorElement.removeEventListener("click", handleFocus, true);
      };
    }, [onFocus]);

    // Expose editor ref and scroll method via forwardRef
    React.useImperativeHandle(
      ref,
      () => ({
        scrollToHeading: (headingIndex) => {
          const quill = quillRef.current?.getEditor();
          if (!quill || !quill.root) return;

          const editorElement = quill.root;
          const headingElements = editorElement.querySelectorAll("h1, h2, h3");

          if (headingElements[headingIndex]) {
            headingElements[headingIndex].scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        },
        getEditorElement: () => quillRef.current?.getEditor()?.root || null,
        getQuillInstance: () => quillRef.current?.getEditor() || null,
      }),
      [],
    );

    return (
      <>
        <div
          ref={wrapperRef}
          className={`rich-text-editor-wrapper ${hideToolbar ? "toolbar-hidden" : ""}`}
        >
          <ReactQuill
            ref={(el) => {
              quillRef.current = el;
            }}
            theme="snow"
            value={value || ""}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            readOnly={readOnly}
            className="rich-text-editor"
          />
        </div>

        <LinkActionPopup
          isOpen={isPopupOpen}
          position={popupPosition}
          linkUrl={linkContext.existingLink}
          onEdit={handleEditLink}
          onOpen={handleOpenLink}
          onClose={() => setIsPopupOpen(false)}
        />

        <LinkModal
          isOpen={isLinkModalOpen}
          onClose={() => setIsLinkModalOpen(false)}
          onSubmit={handleLinkSubmit}
          onRemove={handleRemoveLink}
          selectedText={linkContext.selectedText}
          existingLink={linkContext.existingLink}
        />

        {/* ── Tab-Mention Suggestion Dropdown ─────────────────────────────── */}
        {mentionOpen &&
          (() => {
            const filteredTabs = tabs.filter(
              (t, i) =>
                i !== activeTabIndex &&
                t.name.toLowerCase().includes(mentionQuery.toLowerCase()),
            );
            if (filteredTabs.length === 0) return null;
            return createPortal(
              <div
                ref={mentionMenuRef}
                style={{
                  position: "absolute",
                  top: mentionPos.top,
                  left: mentionPos.left,
                  zIndex: 99999,
                }}
                className="tab-mention-dropdown"
                onMouseDown={(e) => e.preventDefault()} // Keep editor focus
              >
                <div className="tab-mention-dropdown-header">Jump to tab</div>
                {filteredTabs.map((tab, i) => {
                  const realIdx = tabs.indexOf(tab);
                  return (
                    <button
                      key={realIdx}
                      className={`tab-mention-option${i === mentionSelectedIdx ? " selected" : ""}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertTabMention(tab, realIdx);
                      }}
                    >
                      <span className="tab-mention-option-icon">⇥</span>
                      {tab.name}
                    </button>
                  );
                })}
              </div>,
              document.body,
            );
          })()}
      </>
    );
  },
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
